// Shared, non-destructive annotation painting via the CSS Custom Highlight API.
// Works for both the scroll reading view and the paginated Book View: it finds
// every [data-scene-id] element under `root`, builds that scene's read model,
// resolves each of the scene's text annotations against it, and paints the
// resolved ranges. The DOM is never mutated.

import type { ReviewAnnotation } from "../lib/types.js";
import { buildReadModel, resolveAnchor } from "./anchor.js";
import { highlightKind } from "./constants.js";

export const ALL_KINDS = [
  "review-highlight", "review-strike", "review-comment", "review-flag", "review-active", "review-reading"
];

const PAINT_KINDS = ["review-highlight", "review-strike", "review-comment", "review-flag", "review-active"];

/** Set (or clear) a CSS Custom Highlight registry. No-op where unsupported. */
export function setHighlights(name: string, ranges: Range[]): void {
  const reg = (CSS as unknown as { highlights?: Map<string, unknown> & { delete?: (k: string) => void } }).highlights;
  const Ctor = (window as unknown as { Highlight?: new (...r: Range[]) => unknown }).Highlight;
  if (!reg || !Ctor) return;
  if (ranges.length === 0) { reg.delete?.(name); return; }
  reg.set(name, new Ctor(...ranges));
}

/**
 * Paint all text annotations found under `root` (grouped by scene). `onOrphanFlip`
 * is called when an annotation's resolvability changes, so the caller can persist
 * the orphaned flag. Returns the set of annotation ids that resolved to a range.
 */
export function paintAnnotations(
  root: HTMLElement,
  annotations: ReviewAnnotation[],
  opts: { show: boolean; activeId: string | null },
  onOrphanFlip?: (id: string, orphaned: boolean) => void
): Set<string> {
  for (const k of PAINT_KINDS) setHighlights(k, []);
  const resolvedIds = new Set<string>();
  if (!opts.show) return resolvedIds;

  const bySceneId = new Map<string, ReviewAnnotation[]>();
  for (const ann of annotations) {
    if (ann.anchorType !== "text" || ann.status === "archived" || !ann.sceneId) continue;
    if (!highlightKind(ann.type)) continue;
    let list = bySceneId.get(ann.sceneId);
    if (!list) { list = []; bySceneId.set(ann.sceneId, list); }
    list.push(ann);
  }

  const byKind: Record<string, Range[]> = {};
  const activeRanges: Range[] = [];
  const sceneEls = root.querySelectorAll<HTMLElement>("[data-scene-id]");
  sceneEls.forEach((el) => {
    const list = bySceneId.get(el.dataset.sceneId ?? "");
    if (!list || list.length === 0) return;
    const model = buildReadModel(el);
    for (const ann of list) {
      const resolved = resolveAnchor(model, ann.anchor, ann.selectedText);
      if (resolved.orphaned !== ann.orphaned) onOrphanFlip?.(ann.id, resolved.orphaned);
      if (!resolved.range) continue;
      resolvedIds.add(ann.id);
      const kind = highlightKind(ann.type)!;
      (byKind[kind] ??= []).push(resolved.range);
      if (ann.id === opts.activeId) activeRanges.push(resolved.range.cloneRange());
    }
  });

  for (const [kind, ranges] of Object.entries(byKind)) setHighlights(kind, ranges);
  setHighlights("review-active", activeRanges);
  return resolvedIds;
}
